import React, { useState, useCallback } from "react";
import { Box, Image, Center, Stack, Text, Alert } from "@mantine/core";
import { LoadingIndicator } from "../../generic/LoadingIndicator";
import { useMedia } from "@/hooks";

const isBlobSrc = (src: string | null | undefined): boolean =>
  !!src && src.startsWith("blob:");

export interface ImageContentBlockProps {
  /** URL of the image to display */
  imageUrl: string | null | undefined;

  /** Alternative text for the image */
  alt?: string;

  /** How the image should fit within the container */
  fit?: "contain" | "cover" | "fill" | "scale-down";

  /** Additional CSS class name for styling */
  className?: string;

  themeConfig?: any;

  /** Callback fired when image loads successfully */
  onLoad?: (() => void) | undefined;

  /** Callback fired when image fails to load */
  onError?: ((error: string) => void) | undefined;
}

export const ImageContentBlock: React.FC<ImageContentBlockProps> = ({
  imageUrl,
  alt = "Content image",
  fit = "contain",
  className,
  onLoad,
  onError,
}) => {
  // Resolve the image through the session media cache. When the file is already
  // cached (a previous rotation loaded it), `src` is an in-memory blob URL and
  // `isCached` is true — so we skip the loading spinner entirely and never hit
  // the network again.
  const { src, isCached, originalUrl, reportError } = useMedia(imageUrl);
  const [loading, setLoading] = useState(!isCached);
  const [error, setError] = useState<string | null>(null);
  // If a cached blob fails to render, we fall back to the plain network URL.
  const [useNetworkFallback, setUseNetworkFallback] = useState(false);

  const effectiveSrc = useNetworkFallback ? originalUrl : (src ?? imageUrl);

  const handleImageLoad = useCallback(() => {
    setLoading(false);
    setError(null);
    onLoad?.();
  }, [onLoad]);

  const handleImageError = useCallback(() => {
    // A cached blob failed to render (e.g. a limited TV decoder). Drop it and
    // retry from the network before giving up.
    if (!useNetworkFallback && isBlobSrc(effectiveSrc) && originalUrl) {
      reportError();
      setUseNetworkFallback(true);
      setLoading(true);
      setError(null);
      return;
    }

    setLoading(false);
    const errorMessage = "Failed to load image";
    setError(errorMessage);
    onError?.(errorMessage);
  }, [onError, useNetworkFallback, effectiveSrc, originalUrl, reportError]);

  // Show error state
  if (error) {
    return (
      <Box
        className={`image-content-block image-content-block--error ${
          className || ""
        }`}
        h="100%"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--mantine-spacing-lg)",
        }}
      >
        <Stack align="center" gap="md" maw={400}>
          <Alert
            color="red"
            title="Image Error"
            className="image-content-block__error-alert"
          >
            <Text
              size="sm"
              className="image-content-block__error-message"
              ta="center"
            >
              Unable to display the image. Please check the image URL.
            </Text>
          </Alert>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      className={`image-content-block ${className || ""}`}
      h="100%"
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Loading indicator */}
      {loading && (
        <Center
          className="image-content-block__loading"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
          }}
        >
          <Stack align="center" gap="md">
            <LoadingIndicator
              size="lg"
              className="image-content-block__loading-indicator"
            />
            <Text
              size="sm"
              c="dimmed"
              className="image-content-block__loading-text"
              ta="center"
            >
              Loading image...
            </Text>
          </Stack>
        </Center>
      )}

      {/* Image */}
      <Image
        src={effectiveSrc ?? imageUrl}
        alt={alt}
        fit={fit}
        onLoad={handleImageLoad}
        onError={handleImageError}
        className="image-content-block__image"
        style={{
          width: "100%",
          height: "100%",
          opacity: loading ? 0 : 1,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
    </Box>
  );
};
